<template>
  <div class="civ-card card" :class="cardClass" :style="cardStyle">
    <img
      v-if="displayImage"
      :src="imageSource"
      :alt="imageAlt"
      class="civ-image img-fluid w-100 card-image" />
    <div class="card-body civ-card-body" :class="cardBodyClass">
      <div class="px-space-xs pt-space-sm">
        <h3 :class="titleClass">{{ title }}</h3>

        <!-- title-highlight -->
        <div
          v-if="displayTitleHighlight"
          class="civ-title-highlight mb-space-xs"
          :class="titleHighlightClass"></div>

        <!-- slot -->
        <div>
          <slot />
        </div>
      </div>
    </div>
    <div v-if="displayCta" class="card-footer p-0 border-0 bg-white">
      <div class="row gx-0">
        <div class="col-12">
          <div class="px-space-xs">
            <a
              :target="ctaLinkTarget"
              class="mb-space-sm"
              @click="handleClick"
              @click.middle="handleClick"
              :href="ctaLink"
              :aria-label="ctaText"
              :class="ctaClass"
              >{{ ctaText }}</a
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

interface Props {
  borderVariant?: string;
  displayImage?: boolean;
  imageSource?: string;
  imageAlt?: string;
  displayBorderTop?: boolean;
  title: string;
  titleSize?: string;
  titleVariant?: string;
  displayTitleHighlight?: boolean;
  titleHighlightVariant?: string;
  displayCta?: boolean;
  ctaAsButton?: boolean;
  ctaButtonVariant?: string;
  ctaButtonBlock?: boolean;
  ctaText?: string;
  ctaTextSize?: string;
  ctaTextVariant?: string;
  ctaTextWeight?: string;
  ctaLink?: string;
  ctaLinkTarget?: "_self" | "_blank" | "_parent" | "_top";
  highlightColor?: string;
}

const props = withDefaults(defineProps<Props>(), {
  borderVariant: "light-4",
  displayImage: true,
  imageSource: "https://via.placeholder.com/200x200?text=imageSource",
  imageAlt: "civ image alt",
  displayBorderTop: true,
  titleSize: "medium",
  titleVariant: "dark-3",
  displayTitleHighlight: true,
  titleHighlightVariant: "secondary",
  displayCta: true,
  ctaAsButton: false,
  ctaButtonVariant: "secondary",
  ctaButtonBlock: true,
  ctaText: "Lorem ipsum",
  ctaTextSize: "large",
  ctaTextVariant: "primary",
  ctaTextWeight: "normal",
  ctaLink: "https://asuonline.asu.edu/",
  ctaLinkTarget: "_blank",
  highlightColor: "var( --rds-secondary, #ffc627)",
});

// events
const emits = defineEmits<{
  /**
   *  Emitted when the card is clicked.
   */
  (e: "civCtaClick", event: Event): void;
}>();

const getColor = (color: string) => {
  if (color.includes("var(")) {
    return `${color}`;
  } else if (color.includes("--")) {
    return `var(${color})`;
  } else {
    return color;
  }
};

const cardClass = computed(() => {
  return `border-${props.borderVariant}`;
});
const cardStyle = computed(() => {
  return {
    "border-bottom": `7px solid ${getColor(props.highlightColor)} !important`,
  };
});
const cardBodyClass = computed(() => {
  let classes = `${props.displayBorderTop ? "border-top" : null} `;
  classes += `border-${props.borderVariant}`;
  return classes;
});
const titleClass = computed(() => {
  let classNames = "";
  if (props.displayTitleHighlight) {
    classNames = "mb-space-xs";
  } else {
    classNames = "mb-space-sm";
  }

  return (classNames += ` h3-${props.titleSize} text-${props.titleVariant}`);
});
const titleHighlightClass = computed(() => {
  return `bg-${props.titleHighlightVariant} `;
});
const ctaClass = computed(() => {
  let classNames = "";

  if (props.ctaAsButton) {
    classNames += `btn btn-${props.ctaButtonVariant} `;
  } else {
    classNames += `d-inline-block `;
  }

  if (props.ctaButtonBlock) {
    classNames += `d-block btn-block `;
  }

  classNames += `fs-${props.ctaTextSize} text-${props.ctaTextVariant} fw-${props.ctaTextWeight}`;

  return classNames;
});

const handleClick = (event: Event) => {
  emits("civCtaClick", event);
};
</script>

<style lang="scss" scoped>
.card-footer a {
  text-decoration: none;
}

.card-footer a:hover {
  text-decoration: underline;
}
.civ-image {
  height: 200px;
  border-radius: 0;
  object-fit: cover;
  object-position: top;
}
.civ-card-body {
  min-height: 125px;
  padding: 0;
}
.civ-title-highlight {
  height: 4px;
  width: 32px;
}
</style>
